import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getRecentJobMatches, getDashboardChartData } from '../services/api';
import './Dashboard.css';

const CHART_PADDING = { top: 20, right: 16, bottom: 32, left: 52 };
const LINE_CHART_HEIGHT = 200;
const LINE_CHART_WIDTH = 640;
const BAR_CHART_HEIGHT = 200;
const BAR_CHART_WIDTH = 640;

/* Build a smooth cubic bezier path through points */
function smoothPath(pts) {
  if (pts.length < 2) return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const cp1x = prev.x + (curr.x - prev.x) * 0.4;
    const cp2x = prev.x + (curr.x - prev.x) * 0.6;
    d += ` C ${cp1x} ${prev.y} ${cp2x} ${curr.y} ${curr.x} ${curr.y}`;
  }
  return d;
}

function MatchQualityChart({ data }) {
  if (!data || data.length === 0) return null;

  const scores = data.map((d) => d.avg_score);
  const rawMin = Math.min(...scores);
  const rawMax = Math.max(...scores);
  const padding = Math.max((rawMax - rawMin) * 0.2, 5);
  const minY = Math.max(0, Math.floor(rawMin - padding));
  const maxY = Math.min(100, Math.ceil(rawMax + padding));
  const rangeY = maxY - minY || 1;

  const W = LINE_CHART_WIDTH;
  const H = LINE_CHART_HEIGHT;
  const pl = CHART_PADDING.left;
  const pr = CHART_PADDING.right;
  const pt = CHART_PADDING.top;
  const pb = CHART_PADDING.bottom;
  const innerW = W - pl - pr;
  const innerH = H - pt - pb;

  const toX = (i) => pl + (i / Math.max(1, data.length - 1)) * innerW;
  const toY = (val) => pt + innerH - ((val - minY) / rangeY) * innerH;

  const pts = data.map((d, i) => ({ x: toX(i), y: toY(d.avg_score) }));
  const linePath = smoothPath(pts);

  /* Area path: line + close down to baseline */
  const baselineY = pt + innerH;
  const areaPath = `${linePath} L ${pts[pts.length - 1].x} ${baselineY} L ${pts[0].x} ${baselineY} Z`;

  /* Y-axis tick values — 5 evenly spaced */
  const tickCount = 5;
  const yTicks = Array.from({ length: tickCount }, (_, i) =>
    minY + Math.round((rangeY / (tickCount - 1)) * i)
  );

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${H}`}
      className="dashboard-line-chart"
    >
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* Horizontal grid lines */}
      {yTicks.map((val) => {
        const gy = toY(val);
        return (
          <g key={val}>
            <line
              x1={pl} y1={gy} x2={pl + innerW} y2={gy}
              stroke="#f0eefb" strokeWidth="1"
            />
            <text
              x={pl - 8} y={gy + 4}
              textAnchor="end" fontSize="11" fill="#9ca3af" fontFamily="inherit"
            >
              {val}
            </text>
          </g>
        );
      })}

      {/* Y-axis label */}
      <text
        transform={`translate(13, ${pt + innerH / 2}) rotate(-90)`}
        textAnchor="middle" fontSize="11" fill="#6b7280" fontFamily="inherit" fontWeight="500"
      >
        Match Score
      </text>

      {/* X-axis baseline */}
      <line
        x1={pl} y1={pt + innerH} x2={pl + innerW} y2={pt + innerH}
        stroke="#e5e7eb" strokeWidth="1"
      />

      {/* Area fill */}
      <path d={areaPath} fill="url(#areaGrad)" />

      {/* Line */}
      <path
        d={linePath}
        fill="none"
        stroke="#8b5cf6"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Data points + labels */}
      {pts.map((pt_coord, i) => (
        <g key={i}>
          {/* X-axis label */}
          <text
            x={pt_coord.x}
            y={H - 10}
            textAnchor="middle"
            fontSize="11"
            fill="#9ca3af"
            fontFamily="inherit"
          >
            {data[i].week_label}
          </text>

          {/* Score label above dot */}
          <text
            x={pt_coord.x}
            y={pt_coord.y - 10}
            textAnchor="middle"
            fontSize="11"
            fontWeight="600"
            fill="#7c3aed"
            fontFamily="inherit"
          >
            {Math.round(data[i].avg_score)}
          </text>

          {/* Dot outer ring */}
          <circle cx={pt_coord.x} cy={pt_coord.y} r={6} fill="white" stroke="#8b5cf6" strokeWidth="2" />
          {/* Dot inner */}
          <circle cx={pt_coord.x} cy={pt_coord.y} r={3} fill="#8b5cf6" />

          {/* Native SVG tooltip */}
          <title>{`${data[i].week_label}: ${Math.round(data[i].avg_score)}`}</title>
        </g>
      ))}
    </svg>
  );
}

function SupplyVsDemandChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxJobs = Math.max(...data.map((d) => d.jobs_count), 1);
  const maxCandidates = Math.max(...data.map((d) => d.candidates_count), 1);
  const maxVal = Math.max(maxJobs, maxCandidates, 1);
  const w = BAR_CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
  const h = BAR_CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;
  const barGroupWidth = w / data.length;
  const barWidth = Math.min(barGroupWidth * 0.35, 24);
  const gap = 4;
  return (
    <svg width="100%" viewBox={`0 0 ${BAR_CHART_WIDTH} ${BAR_CHART_HEIGHT}`} className="dashboard-bar-chart">
      <line x1={CHART_PADDING.left} y1={CHART_PADDING.top} x2={CHART_PADDING.left} y2={CHART_PADDING.top + h} stroke="#e5e7eb" strokeWidth="1" />
      <line x1={CHART_PADDING.left} y1={CHART_PADDING.top + h} x2={CHART_PADDING.left + w} y2={CHART_PADDING.top + h} stroke="#e5e7eb" strokeWidth="1" />
      {data.map((d, i) => {
        const cx = CHART_PADDING.left + (i + 0.5) * barGroupWidth;
        const jobsH = (d.jobs_count / maxVal) * h;
        const candH = (d.candidates_count / maxVal) * h;
        return (
          <g key={i}>
            <rect x={cx - barWidth - gap / 2} y={CHART_PADDING.top + h - jobsH} width={barWidth} height={jobsH} rx="4" fill="#3b82f6" />
            <rect x={cx + gap / 2} y={CHART_PADDING.top + h - candH} width={barWidth} height={candH} rx="4" fill="#22c55e" />
            <text x={cx} y={BAR_CHART_HEIGHT - 8} textAnchor="middle" fontSize="11" fill="#6b7280">{d.month_label}</text>
          </g>
        );
      })}
      <g transform={`translate(${CHART_PADDING.left - 8}, ${CHART_PADDING.top + h / 2})`}>
        <text textAnchor="end" fontSize="10" fill="#6b7280">{Math.ceil(maxVal)}</text>
      </g>
    </svg>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    active_jobs: 0,
    total_candidates: 0,
    matches_generated: 0,
    avg_match_score: null,
    match_distribution: {
      strong: 0,
      medium: 0,
      weak: 0,
    },
  });
  const [recentMatches, setRecentMatches] = useState([]);
  const [matchQualityOverTime, setMatchQualityOverTime] = useState([]);
  const [supplyVsDemand, setSupplyVsDemand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, matchesData, chartData] = await Promise.all([
        getDashboardStats(),
        getRecentJobMatches(),
        getDashboardChartData(),
      ]);
      
      setStats(statsData);
      setRecentMatches(matchesData);
      setMatchQualityOverTime(chartData.match_quality_over_time || []);
      setSupplyVsDemand(chartData.supply_vs_demand || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatMatchTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Last run: < 1 hour ago';
    if (diffHours < 24) return `Last run: ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `Last run: ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const calculateMatchPercentage = () => {
    const total = stats.match_distribution.strong + stats.match_distribution.medium + stats.match_distribution.weak;
    if (total === 0) return { strong: 0, medium: 0, weak: 0 };
    
    return {
      strong: ((stats.match_distribution.strong / total) * 100).toFixed(1),
      medium: ((stats.match_distribution.medium / total) * 100).toFixed(1),
      weak: ((stats.match_distribution.weak / total) * 100).toFixed(1),
    };
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error">Error loading dashboard: {error}</div>
      </div>
    );
  }

  const matchPercentages = calculateMatchPercentage();

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="dashboard-subtitle">Executive overview of your recruitment pipeline</p>
      </div>

      {/* Stat Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value">{stats.active_jobs}</span>
          <span className="stat-label">Active Jobs</span>
        </div>
        <span className="stat-sep" aria-hidden="true" />
        <div className="stat-item">
          <span className="stat-value">{stats.total_candidates.toLocaleString()}</span>
          <span className="stat-label">Candidates</span>
        </div>
        <span className="stat-sep" aria-hidden="true" />
        <div className="stat-item">
          <span className="stat-value">{stats.matches_generated.toLocaleString()}</span>
          <span className="stat-label">Matches Generated</span>
        </div>
        <span className="stat-sep" aria-hidden="true" />
        <div className="stat-item">
          <span className="stat-value">
            {stats.avg_match_score !== null ? stats.avg_match_score : '--'}
          </span>
          <span className="stat-label">Avg Match Score</span>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Match Quality Over Time</h3>
            <span className="chart-period">Last 30 days</span>
          </div>
          <div className="chart-body chart-line">
            {matchQualityOverTime.length === 0 ? (
              <div className="empty-chart">No match data available yet</div>
            ) : (
              <MatchQualityChart data={matchQualityOverTime} />
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Supply vs Demand</h3>
            <span className="chart-period">This month</span>
          </div>
          <div className="svd-legend">
            <span className="svd-legend-item">
              <span className="svd-legend-dot" style={{ background: '#3b82f6' }} />
              Romeo <span className="svd-legend-sub">(Jobs)</span>
            </span>
            <span className="svd-legend-item">
              <span className="svd-legend-dot" style={{ background: '#22c55e' }} />
              Juliet <span className="svd-legend-sub">(Candidates)</span>
            </span>
          </div>
          <div className="chart-body chart-bars">
            {supplyVsDemand.length === 0 ? (
              <div className="empty-chart">No data available yet</div>
            ) : (
              <SupplyVsDemandChart data={supplyVsDemand} />
            )}
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="dashboard-bottom-section">
        {/* Recent Job Matches */}
        <div className="recent-matches-card">
          <div className="matches-header">
            <h3>Recent Job Matches</h3>
            {recentMatches.length > 0 && (
              <button className="view-all-btn" onClick={() => navigate('/matchmaker')}>
                View all
              </button>
            )}
          </div>
          
          {recentMatches.length === 0 ? (
            <div className="empty-state">
              <p>No matches generated yet</p>
            </div>
          ) : (
            <div className="matches-list">
              {recentMatches.map((match, index) => (
                <div 
                  key={index} 
                  className="match-item"
                  onClick={() => navigate(`/matchmaker/${match.job_id}`)}
                >
                  <div className="match-info">
                    <h4 className="match-title">{match.job_title}</h4>
                    <div className="match-meta">
                      <span>{match.company}</span>
                      <span className="separator">•</span>
                      <span>{match.location}</span>
                    </div>
                  </div>
                  <div className="match-right">
                    <div className="match-score-display">
                      <span className="score-label">Top Score</span>
                      <span className="score-value">{Math.round(match.top_score)}</span>
                    </div>
                  </div>
                  {match.matched_at && (
                    <div className="match-time">{formatMatchTime(match.matched_at)}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Match Distribution */}
        <div className="distribution-card">
          <h3>Match Distribution</h3>
          
          {stats.matches_generated === 0 ? (
            <div className="empty-state">
              <p>No match distribution data yet</p>
            </div>
          ) : (
            <>
              <div className="distribution-chart">
                <div className="distribution-placeholder">
                  <div
                    className="pie-placeholder"
                    style={{
                      background: (() => {
                        const strong = parseFloat(matchPercentages.strong) || 0;
                        const medium = parseFloat(matchPercentages.medium) || 0;
                        const weak = parseFloat(matchPercentages.weak) || 0;
                        const deg = (p) => (p / 100) * 360;
                        const d1 = deg(strong);
                        const d2 = d1 + deg(medium);
                        const d3 = d2 + deg(weak);
                        if (d3 <= 0) return '#e5e7eb';
                        return `conic-gradient(
                          #22c55e 0deg ${d1}deg,
                          #fbbf24 ${d1}deg ${d2}deg,
                          #d1d5db ${d2}deg 360deg
                        )`;
                      })(),
                    }}
                  />
                </div>
              </div>
              
              <div className="distribution-legend">
                <div className="legend-item">
                  <span className="legend-dot strong"></span>
                  <span className="legend-label">Strong Match</span>
                  <span className="legend-value">
                    {stats.match_distribution.strong} ({matchPercentages.strong}%)
                  </span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot medium"></span>
                  <span className="legend-label">Medium Match</span>
                  <span className="legend-value">
                    {stats.match_distribution.medium} ({matchPercentages.medium}%)
                  </span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot weak"></span>
                  <span className="legend-label">Weak Match</span>
                  <span className="legend-value">
                    {stats.match_distribution.weak} ({matchPercentages.weak}%)
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
